import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import {
  PencilIcon, TrashIcon, PaperAirplaneIcon,
  CheckCircleIcon, CurrencyDollarIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { PageHeader } from '../../components/layout';
import {
  Card, CardBody, CardHeader, CardTitle, Button, StatusBadge,
  PageLoader, Modal, ModalBody, ModalFooter, Input, Select
} from '../../components/ui';
import { invoiceService } from '../../services';
import { useUIStore } from '../../stores';
import { useForm } from 'react-hook-form';

export default function InvoiceDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { confirm } = useUIStore();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  
  const { register, handleSubmit, reset } = useForm();
  
  useEffect(() => {
    fetchInvoice();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  
  const fetchInvoice = async () => {
    try {
      const data = await invoiceService.getInvoice(id);
      setInvoice(data);
    } catch (error) {
      toast.error(t('errors.notFound'));
      navigate('/invoices');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDelete = async () => {
    const confirmed = await confirm({
      title: t('common.delete'),
      message: 'Are you sure you want to delete this invoice?',
      confirmText: t('common.delete'),
      variant: 'danger'
    });
    
    if (confirmed) {
      try {
        await invoiceService.deleteInvoice(id);
        toast.success(t('success.deleted'));
        navigate('/invoices');
      } catch (error) {
        toast.error(error.response?.data?.message || t('errors.serverError'));
      }
    }
  };
  
  const handleSend = async () => {
    try {
      await invoiceService.sendInvoice(id);
      toast.success(t('success.sent'));
      fetchInvoice();
    } catch (error) {
      toast.error(error.response?.data?.message || t('errors.serverError'));
    }
  };
  
  const handleMarkPaid = async () => {
    try {
      await invoiceService.markAsPaid(id);
      toast.success(t('success.updated'));
      fetchInvoice();
    } catch (error) {
      toast.error(error.response?.data?.message || t('errors.serverError'));
    }
  };
  
  const onPaymentSubmit = async (data) => {
    try {
      await invoiceService.addPayment(id, {
        amount: parseFloat(data.amount),
        method: data.method,
        reference: data.reference,
        notes: data.notes
      });
      toast.success(t('success.created'));
      setShowPaymentModal(false);
      reset();
      fetchInvoice();
    } catch (error) {
      toast.error(error.response?.data?.message || t('errors.serverError'));
    }
  };
  
  const paymentMethods = [
    { value: 'cash', label: 'Cash' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'credit_card', label: 'Credit Card' },
    { value: 'paypal', label: 'PayPal' },
    { value: 'other', label: 'Other' }
  ];
  
  if (loading) {
    return <PageLoader />;
  }
  
  if (!invoice) {
    return null;
  }
  
  return (
    <>
      <Helmet>
        <title>{invoice.invoiceNumber} - ProCTM</title>
      </Helmet>
      
      <PageHeader
        title={invoice.invoiceNumber}
        backUrl="/invoices"
        actions={
          <div className="flex items-center gap-2">
            {invoice.status === 'draft' && (
              <>
                <Button variant="outline" icon={PaperAirplaneIcon} onClick={handleSend}>
                  {t('invoices.send')}
                </Button>
                <Button variant="outline" icon={PencilIcon} onClick={() => navigate(`/invoices/${id}/edit`)}>
                  {t('common.edit')}
                </Button>
              </>
            )}
            {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
              <>
                <Button variant="secondary" icon={CurrencyDollarIcon} onClick={() => setShowPaymentModal(true)}>
                  {t('invoices.addPayment')}
                </Button>
                <Button variant="accent" icon={CheckCircleIcon} onClick={handleMarkPaid}>
                  {t('invoices.markPaid')}
                </Button>
              </>
            )}
            <Button variant="danger" icon={TrashIcon} onClick={handleDelete}>
              {t('common.delete')}
            </Button>
          </div>
        }
      />
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Invoice Details */}
        <div className="lg:col-span-2">
          <Card>
            <CardBody>
              <div className="flex justify-between items-start mb-8">
                <div>
                  <h2 className="text-2xl font-bold mb-2">{invoice.invoiceNumber}</h2>
                  <StatusBadge status={invoice.status} />
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">{t('invoices.issueDate')}</p>
                  <p className="font-medium">{dayjs(invoice.issueDate).format('MMM D, YYYY')}</p>
                  <p className="text-sm text-gray-500 mt-2">{t('invoices.dueDate')}</p>
                  <p className={`font-medium ${invoice.isOverdue ? 'text-danger' : ''}`}>
                    {dayjs(invoice.dueDate).format('MMM D, YYYY')}
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-8 mb-8">
                <div>
                  <p className="text-sm text-gray-500 mb-2">Bill To</p>
                  <Link to={`/clients/${invoice.client?._id}`} className="hover:underline">
                    <p className="font-medium">{invoice.client?.name}</p>
                  </Link>
                  {invoice.client?.company && <p className="text-sm">{invoice.client.company}</p>}
                  {invoice.client?.email && <p className="text-sm text-gray-500">{invoice.client.email}</p>}
                </div>
                {invoice.project && (
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Project</p>
                    <Link to={`/projects/${invoice.project._id}`} className="hover:underline">
                      <p className="font-medium">{invoice.project.name}</p>
                    </Link>
                  </div>
                )}
              </div>
              
              {/* Items Table */}
              <table className="w-full mb-8">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 text-sm font-medium text-gray-500">Description</th>
                    <th className="text-right py-3 text-sm font-medium text-gray-500">Qty</th>
                    <th className="text-right py-3 text-sm font-medium text-gray-500">Unit Price</th>
                    <th className="text-right py-3 text-sm font-medium text-gray-500">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.items?.map((item, index) => (
                    <tr key={index} className="border-b border-gray-100">
                      <td className="py-3">{item.description}</td>
                      <td className="py-3 text-right">{item.quantity}</td>
                      <td className="py-3 text-right">${item.unitPrice.toFixed(2)}</td>
                      <td className="py-3 text-right font-medium">${item.total.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {/* Totals */}
              <div className="flex justify-end">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('invoices.subtotal')}</span>
                    <span>${invoice.subtotal?.toFixed(2)}</span>
                  </div>
                  {invoice.taxTotal > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">{t('invoices.tax')}</span>
                      <span>${invoice.taxTotal.toFixed(2)}</span>
                    </div>
                  )}
                  {invoice.discountTotal > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">{t('invoices.discount')}</span>
                      <span>-${invoice.discountTotal.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-200">
                    <span>{t('invoices.total')}</span>
                    <span>${invoice.total?.toFixed(2)}</span>
                  </div>
                  {invoice.amountPaid > 0 && (
                    <>
                      <div className="flex justify-between text-success">
                        <span>{t('invoices.amountPaid')}</span>
                        <span>-${invoice.amountPaid.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-lg font-bold">
                        <span>{t('invoices.amountDue')}</span>
                        <span className={invoice.amountDue > 0 ? 'text-danger' : 'text-success'}>
                          ${invoice.amountDue.toFixed(2)}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
              
              {/* Notes */}
              {invoice.notes && (
                <div className="mt-8 pt-8 border-t border-gray-200">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">{t('common.notes')}</h3>
                  <p className="text-sm">{invoice.notes}</p>
                </div>
              )}
            </CardBody>
          </Card>
        </div>
        
        {/* Payments */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle>Payments</CardTitle>
            </CardHeader>
            <CardBody>
              {invoice.payments?.length > 0 ? (
                <div className="space-y-4">
                  {invoice.payments.map((payment, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-success">${payment.amount.toFixed(2)}</p>
                          <p className="text-sm text-gray-500">{payment.method}</p>
                        </div>
                        <p className="text-xs text-gray-400">
                          {dayjs(payment.date).format('MMM D, YYYY')}
                        </p>
                      </div>
                      {payment.reference && (
                        <p className="text-xs text-gray-500 mt-1">Ref: {payment.reference}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm text-center py-4">No payments recorded</p>
              )}
            </CardBody>
          </Card>
        </div>
      </div>
      
      {/* Add Payment Modal */}
      <Modal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        title={t('invoices.addPayment')}
      >
        <form onSubmit={handleSubmit(onPaymentSubmit)}>
          <ModalBody>
            <div className="space-y-4">
              <Input
                label="Amount"
                type="number"
                step="0.01"
                max={invoice.amountDue}
                defaultValue={invoice.amountDue}
                {...register('amount', { required: true, min: 0.01 })}
              />
              <Select
                label="Payment Method"
                options={paymentMethods}
                {...register('method', { required: true })}
              />
              <Input
                label="Reference"
                placeholder="Transaction ID or reference"
                {...register('reference')}
              />
              <Input
                label="Notes"
                {...register('notes')}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" onClick={() => setShowPaymentModal(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit">{t('common.add')}</Button>
          </ModalFooter>
        </form>
      </Modal>
    </>
  );
}
